import * as React from "react";
import AppIcon from "../components/AppIcon";
import axios from "axios";
import { take } from "lodash";
import { useNavigate } from "react-router-dom";
import { useUser } from "../utils/AccountStore";

export interface Exhibitor {
    id: string;
    name: string;
    logo: string;
    description: string;
    isTreasure: boolean;
    location: string;
    website: string;
}

// In-memory cache for exhibitors
let exhibitorsCache: Exhibitor[] | null = null;
let exhibitorsCacheTime: number = 0;
const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds

export const GetExhibitors = async () => {
    const now = Date.now();
    const timeSinceLastFetch = now - exhibitorsCacheTime;

    // Return cached data if it's less than 1 hour old
    if (exhibitorsCache && timeSinceLastFetch < ONE_HOUR) {
        return { status: 200, data: exhibitorsCache };
    }

    // Fetch fresh data from server
    const response = await axios.get(`${Server}/api/exhibitors`);
    
    // Update cache
    if (response.status === 200) {
        exhibitorsCache = response.data;
        exhibitorsCacheTime = now;
    }
    
    return response;
};

export const ExhibitorRow = ({ exhibitor, onClick }: { exhibitor: Exhibitor; onClick: () => void }) => {
    const [imageError, setImageError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    const handleImageError = () => {
        setImageError(true);
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    return (
        <div
            className="group relative bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            style={{ borderLeft: `4px solid #DB1C7D` }}
            onClick={onClick}
        >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300" style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #7999BA 100%)" }}></div>

            <div className="relative z-10 flex items-center gap-6">
                <div className="relative">
                    {!exhibitor.logo || imageError ? (
                        <div className="w-20 h-20 rounded-xl shadow-md flex items-center justify-center text-2xl" style={{ backgroundColor: "rgba(219, 28, 125, 0.1)", color: "#DB1C7D" }}>
                            <AppIcon kind="store" />
                        </div>
                    ) : (
                        <img src={exhibitor.logo} alt={`${exhibitor.name} logo`} className="w-20 h-20 object-contain rounded-xl shadow-md" onError={handleImageError} onLoad={handleImageLoad} />
                    )}
                    {exhibitor.isTreasure && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: "#DDB649" }}>
                            <AppIcon kind="gem" />
                        </div>
                    )}
                </div>

                <div className="flex-1">
                    <h4 className="font-bold text-xl mb-2" style={{ color: "#013A51" }}>
                        {exhibitor.name}
                    </h4>
                    {exhibitor.location && (
                        <p className="text-sm mb-1" style={{ color: "#708372" }}>
                            <AppIcon kind="marker" /> {exhibitor.location}
                        </p>
                    )}
                    {exhibitor.description && (
                        <p className="text-sm opacity-80 line-clamp-2" style={{ color: "#8B6F4C" }}>
                            {exhibitor.description}
                        </p>
                    )}
                </div>

                <div className="flex items-center">
                    <span className="text-2xl" style={{ color: "#DB1C7D" }}>
                        →
                    </span>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ backgroundColor: "#DB1C7D" }}></div>
        </div>
    );
};

const Exhibitors = () => {
    const [exhibitors, setExhibitors] = React.useState<Exhibitor[]>([]);
    const [filteredExhibitors, setFilteredExhibitors] = React.useState<Exhibitor[]>([]);
    const [sort, setSort] = React.useState("name");
    const [sortOrder, setSortOrder] = React.useState("ascending");
    const [filter, setFilter] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [pageSize, setPageSize] = React.useState(10);
    const [showSearchFields, setShowSearchFields] = React.useState(false);

    const { user, getUser } = useUser();
    const navigate = useNavigate();

    React.useEffect(() => {
        GetExhibitors()
            .then((res) => {
                if (res.status === 200) {
                    setExhibitors(res.data);
                    setLoading(false);
                } else {
                    alert("Error getting exhibitors");
                    navigate("/home");
                }
            })
            .catch(() => {
                alert("Error getting exhibitors");
                navigate("/home");
            });

        getUser();
    }, [navigate, getUser]);

    React.useEffect(() => {
        setFilteredExhibitors(exhibitors.filter((e: Exhibitor) => e.name.toLowerCase().includes(filter.toLowerCase())));
        setPageSize(10);
    }, [exhibitors, filter]);

    React.useEffect(() => {
        handleSort();
        setPageSize(10);
    }, [sort, sortOrder]);

    const handleSort = () => {
        const sorted = [...filteredExhibitors].sort((a, b) => {
            const keyA = (a as any)[sort].toLowerCase();
            const keyB = (b as any)[sort].toLowerCase();
            return sortOrder === "ascending" ? keyA.localeCompare(keyB) : keyB.localeCompare(keyA);
        });
        setFilteredExhibitors(sorted);
    };

    const toggleSort = (field: "name" | "location") => {
        if (sort !== field) {
            setSort(field);
            setSortOrder("ascending");
        } else {
            setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
        }
    };

    return (
        <div
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            <div className="min-h-screen backdrop-blur-sm" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                {/* Header Section */}
                <div className="pt-8 pb-6">
                    <div className="max-w-6xl mx-auto px-8">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate("/home")}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                            >
                                <span className="text-white text-xl"><AppIcon kind="home" /></span>
                            </button>

                            <div className="text-center w-full">
                                <h1 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    Our Artisan Vendors
                                </h1>
                                <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: "#DB1C7D" }}></div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowSearchFields(!showSearchFields)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                    style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                >
                                    <span className="text-white text-xl"><AppIcon kind="search" /></span>
                                </button>
                            </div>
                        </div>

                        {/* Search and Sort Controls */}
                        {showSearchFields && (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 shadow-lg mb-8 animate-fade-in" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                    {/* Search */}
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: "#013A51" }}>
                                            Search Vendors
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search by name..."
                                            value={filter}
                                            onChange={(e) => setFilter(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200"
                                            style={{
                                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                            }}
                                        />
                                    </div>

                                    {/* Sort Field */}
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: "#013A51" }}>
                                            Sort By
                                        </label>
                                        <select
                                            value={sort}
                                            onChange={(e) => toggleSort(e.target.value as "name" | "location")}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200"
                                            style={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
                                        >
                                            <option value="name">Vendor Name</option>
                                            <option value="location">Location</option>
                                        </select>
                                    </div>

                                    {/* Sort Order */}
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: "#013A51" }}>
                                            Order
                                        </label>
                                        <button
                                            onClick={() => setSortOrder(sortOrder === "ascending" ? "descending" : "ascending")}
                                            className="w-full px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex items-center justify-center gap-2"
                                            style={{ backgroundColor: "rgba(112, 131, 114, 0.9)" }}
                                        >
                                            <span className="text-white font-semibold">{sortOrder === "ascending" ? "↑ A-Z" : "↓ Z-A"}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Stats when search is hidden */}
                        {!showSearchFields && (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-4 shadow-lg mb-8 animate-fade-in" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="text-center">
                                    <p className="text-lg font-semibold" style={{ color: "#013A51" }}>
                                        {exhibitors.length} Amazing Vendors
                                    </p>
                                    <p className="text-sm" style={{ color: "#708372" }}>
                                        Browse our collection of talented artisans and local businesses
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="px-8 pb-8">
                    <div className="max-w-6xl mx-auto">
                        {loading ? (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="mb-4">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent mx-auto" style={{ borderTopColor: "#DB1C7D" }}></div>
                                </div>
                                <h3 className="text-xl font-bold" style={{ color: "#013A51" }}>
                                    Loading Our Amazing Vendors...
                                </h3>
                            </div>
                        ) : filteredExhibitors.length === 0 ? (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="mb-4">
                                    <span className="text-6xl"><AppIcon kind="search" /></span>
                                </div>
                                <h3 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    No Vendors Found
                                </h3>
                                <p className="text-lg" style={{ color: "#708372" }}>
                                    Try adjusting your search criteria or check back later.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Vendor Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                                    {take(filteredExhibitors, pageSize).map((exhibitor: Exhibitor) => (
                                        <ExhibitorRow key={exhibitor.id} exhibitor={exhibitor} onClick={() => navigate(`/exhibitorDetails/${exhibitor.id}`)} />
                                    ))}
                                </div>

                                {/* Load More Button */}
                                {filteredExhibitors.length > pageSize && (
                                    <div className="text-center">
                                        <button
                                            className="px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white"
                                            style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                            onClick={() => setPageSize(pageSize + 10)}
                                        >
                                            Load More Vendors ({filteredExhibitors.length - pageSize} remaining)
                                        </button>
                                    </div>
                                )}

                                {/* Results Summary */}
                                <div className="mt-8 text-center">
                                    <p className="text-sm" style={{ color: "#708372" }}>
                                        Showing {Math.min(pageSize, filteredExhibitors.length)} of {filteredExhibitors.length} vendors
                                        {filter && ` (filtered from ${exhibitors.length} total)`}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Exhibitors;
